import { useCallback, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { sessionAssignationStore, useSessionAssignationStore } from "@context/sessionAssignation";
import {
  canCreateOccurrence,
  canDeleteOccurrence,
  canPatchOccurrence,
  SessionAssignationError,
} from "@services/collections/session-assignation";
import type {
  SessionAssignationRow,
  SessionAssignationRowPatch,
} from "@services/types/session-assignation";
import type {
  SessionAssignationActionResult,
  SessionAssignationCreation,
  SessionAssignationEditorTarget,
  SessionAssignationResourceTarget,
} from "../../types/session-assignation";
import {
  canEditOccurrence,
  emptiedSectionsByDeletion,
  isOccurrenceProtected,
} from "../../utils/session-assignation";

export const useSessionAssignationEditing = (courseId?: string) => {
  const draft = useSessionAssignationStore(
    useShallow(state => ({
      rows: state.rows,
      originalRows: state.originalRows,
      sections: state.sections,
      weeks: state.weeks,
      blocks: state.blocks,
      policy: state.policy,
      filters: state.filters,
      phase: state.phase,
      issues: state.issues,
      editorDirty: state.editorDirty,
    })),
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [creation, setCreation] = useState<SessionAssignationCreation | null>(null);
  const [editor, setEditor] = useState<SessionAssignationEditorTarget | null>(null);
  const [resource, setResource] = useState<SessionAssignationResourceTarget | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [resourceOpen, setResourceOpen] = useState(false);
  const [drawerDirty, setDrawerDirty] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);
  const [deletionIds, setDeletionIds] = useState<string[] | null>(null);
  const selected = useMemo(() => {
    const ids = new Set(selectedIds);
    return draft.rows.filter(row => ids.has(row.localId));
  }, [draft.rows, selectedIds]);
  const emptyingSections = deletionIds ? emptiedSectionsByDeletion(draft, deletionIds) : [];
  const canDelete =
    canDeleteOccurrence(draft.policy) &&
    selected.length > 0 &&
    selected.every(row => row.pendingState !== "deleted" && !isOccurrenceProtected(row, draft));
  const canDuplicate =
    canCreateOccurrence(draft.policy) &&
    selected.length > 0 &&
    selected.every(
      row =>
        canEditOccurrence(row, draft) &&
        (!row.classrooms.length || draft.policy.abilities.can_assign_classrooms) &&
        (!row.instructors.length || draft.policy.abilities.can_assign_instructors),
    );

  useEffect(() => {
    const dirty = !!creation || drawerDirty;
    if (sessionAssignationStore.getState().editorDirty !== dirty)
      sessionAssignationStore.getState().setEditorDirty(dirty);
  }, [creation, drawerDirty]);

  const attempt = (action: () => boolean | string | null): SessionAssignationActionResult => {
    try {
      const accepted = action();
      setIssue(accepted ? null : "ACTION_NOT_ALLOWED");
      return accepted ? { ok: true } : { ok: false, code: "ACTION_NOT_ALLOWED" };
    } catch (error) {
      const code = error instanceof SessionAssignationError ? error.code : "ACTION_FAILED";
      setIssue(code);
      return { ok: false, code };
    }
  };

  const applyCreation = (next: SessionAssignationCreation): SessionAssignationActionResult => {
    if (!next.date || !next.scheduleMode) {
      setCreation(next);
      return { ok: true };
    }
    const added = attempt(() =>
      sessionAssignationStore.getState().addRow({
        ...next,
        date: next.date,
        scheduleMode: next.scheduleMode,
        intervalId: "",
        dayId: null,
        blocksIds: [],
      }),
    );
    if (added.ok) setCreation(null);
    return added;
  };

  const closeDrawer = useCallback(() => {
    setEditorOpen(false);
    setResourceOpen(false);
    setDrawerDirty(false);
  }, []);

  const finishEditorClose = useCallback(() => {
    setEditor(null);
    setEditorOpen(false);
    setDrawerDirty(false);
  }, []);
  const finishResourceClose = useCallback(() => {
    setResource(null);
    setResourceOpen(false);
    setDrawerDirty(false);
  }, []);

  const editTime = (row: SessionAssignationRow) => {
    if (!canEditOccurrence(row, sessionAssignationStore.getState())) return;
    setEditor({ kind: "edit", values: row });
    setEditorOpen(true);
  };

  return {
    draft,
    selected,
    selectedIds,
    creation,
    editor,
    resource,
    editorOpen,
    resourceOpen,
    issue,
    deletionIds,
    emptyingSections,
    setDrawerDirty,
    canDelete,
    canDuplicate,
    canRestore:
      selected.some(row => row.pendingState !== "unchanged") &&
      selected.every(row => {
        const original = draft.originalRows.find(item => item.localId === row.localId);
        if (!original) return canCreateOccurrence(draft.policy);
        return (
          !isOccurrenceProtected(row, draft) &&
          (row.pendingState === "deleted"
            ? canDeleteOccurrence(draft.policy)
            : canPatchOccurrence(row, original, draft.policy))
        );
      }),
    toggleSelection: (ids: string[], checked: boolean) =>
      setSelectedIds(current =>
        checked ? [...new Set([...current, ...ids])] : current.filter(id => !ids.includes(id)),
      ),
    create: (sectionId: string) => {
      if (creation || !canCreateOccurrence(sessionAssignationStore.getState().policy)) return;
      setCreation({
        sectionId,
        date: null,
        scheduleMode: null,
        startingTime: null,
        endingTime: null,
        blocksCountInput: "",
        classrooms: [],
        instructors: [],
      });
      setIssue(null);
    },
    cancelCreation: () => {
      setCreation(null);
      closeDrawer();
      setIssue(null);
    },
    changeCreationDate: (date: string) => creation && applyCreation({ ...creation, date }),
    editCreationTime: () => {
      if (!creation) return;
      setEditor({ kind: "create", values: creation });
      setEditorOpen(true);
    },
    editTime,
    patchRow: (rowId: string, patch: SessionAssignationRowPatch) =>
      attempt(() => sessionAssignationStore.getState().updateRow(rowId, patch)).ok,
    applyEditor: (patch: SessionAssignationRowPatch): SessionAssignationActionResult => {
      if (!editorOpen || !editor) return { ok: false, code: "ACTION_NOT_ALLOWED" };
      const accepted =
        editor.kind === "create" && creation
          ? applyCreation({ ...creation, ...patch })
          : editor.kind === "edit"
            ? attempt(() =>
                sessionAssignationStore.getState().updateRow(editor.values.localId, patch),
              )
            : { ok: false as const, code: "ACTION_NOT_ALLOWED" };
      if (accepted.ok) closeDrawer();
      return accepted;
    },
    openResources: (
      field: SessionAssignationResourceTarget["field"],
      row: SessionAssignationRow | null,
    ) => {
      const current = sessionAssignationStore.getState();
      if (
        (field === "instructors" && !courseId) ||
        current.phase !== "editing" ||
        !current.policy.abilities[`can_assign_${field}`] ||
        (row ? !canEditOccurrence(row, current) : !canCreateOccurrence(current.policy))
      )
        return;
      const values = row ?? creation;
      if (!values) return;
      setResource({
        field,
        rowId: row?.localId ?? null,
        classrooms: values.classrooms,
        instructors: values.instructors,
      });
      setResourceOpen(true);
    },
    applyResources: (patch: SessionAssignationRowPatch): SessionAssignationActionResult => {
      if (!resourceOpen || !resource || (resource.field === "instructors" && !courseId))
        return { ok: false, code: "ACTION_NOT_ALLOWED" };
      const accepted = resource.rowId
        ? attempt(() => sessionAssignationStore.getState().updateRow(resource.rowId, patch))
        : creation
          ? applyCreation({ ...creation, ...patch })
          : { ok: false as const, code: "ACTION_NOT_ALLOWED" };
      if (accepted.ok) closeDrawer();
      return accepted;
    },
    closeDrawer,
    finishEditorClose,
    finishResourceClose,
    deleteSelected: () => {
      if (!canDelete || draft.phase !== "editing") return;
      const ids = selected.map(row => row.localId);
      if (emptiedSectionsByDeletion(sessionAssignationStore.getState(), ids).length)
        setDeletionIds(ids);
      else attempt(() => sessionAssignationStore.getState().deleteRows(ids));
    },
    cancelDeletion: () => setDeletionIds(null),
    confirmDeletion: () => {
      if (
        deletionIds &&
        attempt(() => sessionAssignationStore.getState().deleteRows(deletionIds)).ok
      )
        setDeletionIds(null);
    },
    restoreSelected: () =>
      attempt(() =>
        sessionAssignationStore.getState().restoreRows(selected.map(row => row.localId)),
      ),
    undoDeletion: (rowId: string) =>
      attempt(() => sessionAssignationStore.getState().undoDeletion(rowId)),
    duplicateSelected: () => {
      if (!canDuplicate || draft.phase !== "editing" || creation) return;
      attempt(
        () => !!sessionAssignationStore.getState().duplicateRows(selected.map(row => row.localId)),
      );
    },
  };
};
